'use client';

import { NavbarAuth } from '@/components/navbar-auth';
import OptimizedLogo from '@/components/optimized-logo';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import RegisterDialog from './RegisterDialog';
import { AuthGuard } from './auth-guard';
import { Menu } from 'lucide-react';
import MobileNavTrigger from './mobileNavTrigger';
import MobileNavOverlay from './mobileNavOverlay';

interface NavItem {
  icon: string;
  label: string;
  link: string;
  activeIcon: string;
  needsAuth?: boolean;
  component?: (children: React.ReactNode, label: string) => React.ReactNode;
  isActive?: boolean;
  isBold?: boolean;
}

export const Navbar = () => {
  const pathname = usePathname();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const user = useAuthStore((state) => state.user);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const NAV_ITEMS: NavItem[] = [
    {
      icon: '/home.svg',
      label: 'Home',
      link: user ? '/home' : '/',
      activeIcon: '/home-active.svg',
      needsAuth: false,
      isActive: pathname === '/' || pathname.startsWith('/home'),
      isBold: false,
    },
    // {
    //   icon: '/discover.svg',
    //   activeIcon: '/discover-active.svg',
    //   label: 'Discover More',
    //   link: '/discover-more',
    //   needsAuth: false,
    //   isActive: pathname.startsWith('/discover-more'),
    //   isBold: false,
    // },
    {
      icon: '/ask.svg',
      activeIcon: '/ask-active.svg',
      label: 'Add Question',
      link: '/ask-question',
      needsAuth: true,
      component: (children: React.ReactNode, label: string) => (
        <RegisterDialog
          key={label}
          title="You've got something valuable to say."
          description="Create your free account to ask questions, contribute your experience, and help others navigate change."
          redirectUrl="/ask-question"
        >
          {children}
        </RegisterDialog>
      ),
      isActive: pathname.startsWith('/ask-question'),
      isBold: false,
    },
    {
      icon: '/add-story.svg',
      activeIcon: '/add-story-active.svg',
      label: 'Add Story',
      link: '/add-story',
      needsAuth: false,
      isActive: pathname.startsWith('/add-story'),
      isBold: false,
    },
    {
      icon: '/discussions.svg',
      activeIcon: '/discussions-active.svg',
      label: 'Discussions',
      link: '/discussions',
      needsAuth: false,
      isActive: pathname.startsWith('/discussions'),
      isBold: false,
    },
    {
      icon: '/idea.svg',
      activeIcon: '/idea-active.svg',
      label: 'Idea Starters',
      link: '/idea-starters',
      needsAuth: false,
      isActive: pathname.startsWith('/idea-starters'),
      isBold: false,
    },
  ];

  return (
    <AuthGuard>
      <div className="bg-ihs-dark-blue h-20 z-50 py-2 sticky top-0 left-0 text-white px-8 flex items-center justify-between">
        <div className="min-w-[160px]">
          <Link href="/">
            <OptimizedLogo color="white" width={160} height={45} />
          </Link>
        </div>

        <div
          className={cn(
            'hidden xl:flex gap-2 items-center font-medium transition-all duration-300',
            isSearchOpen
              ? 'opacity-0 translate-y-2 pointer-events-none select-none w-1/4'
              : 'opacity-100 translate-y-0'
          )}
          aria-hidden={isSearchOpen}
        >
          {NAV_ITEMS.map((item) => {
            if (item.needsAuth && !user) {
              return item.component?.(
                <span
                  className={cn(
                    'flex items-center cursor-pointer flex-col gap-1 uppercase hover:bg-ihs-blue p-2 rounded-md transition-all duration-200 min-w-[150px] text-center',
                    item.isActive && 'bg-ihs-blue font-bold',
                    item.isBold && 'font-bold'
                  )}
                >
                  {item.isActive ? (
                    <Image
                      src={item.activeIcon}
                      alt={item.label + ' active icon'}
                      width={24}
                      height={24}
                      rel="preload"
                      priority
                    />
                  ) : (
                    <Image
                      src={item.icon}
                      alt={item.label + ' icon'}
                      width={24}
                      height={24}
                      priority
                      rel="preload"
                    />
                  )}
                  <h4>{item.label}</h4>
                </span>,
                item.label
              );
            } else {
              return (
                <Link
                  key={item.label}
                  href={item.link}
                  className={cn(
                    'flex items-center cursor-pointer flex-col gap-1 uppercase hover:bg-ihs-blue p-2 rounded-md transition-all duration-200 min-w-[150px] text-center',
                    item.isActive && 'bg-ihs-blue font-bold',
                    item.isBold && 'font-bold'
                  )}
                >
                  {item.isActive ? (
                    <Image
                      src={item.activeIcon}
                      alt={item.label + ' active icon'}
                      width={24}
                      height={24}
                      rel="preload"
                      priority
                    />
                  ) : (
                    <Image
                      src={item.icon}
                      alt={item.label + ' icon'}
                      width={24}
                      height={24}
                      priority
                      rel="preload"
                    />
                  )}
                  <h4>{item.label}</h4>
                </Link>
              );
            }
          })}
        </div>

        <div className="flex items-center gap-3">
          <NavbarAuth
            isSearchOpen={isSearchOpen}
            setIsSearchOpen={setIsSearchOpen}
          />

          <MobileNavTrigger onOpen={() => setIsMenuOpen(true)} />

          <MobileNavOverlay
            isOpen={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
            navItems={NAV_ITEMS}
          />
        </div>
      </div>
    </AuthGuard>
  );
};
